import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calculator, AlertCircle } from 'lucide-react';

interface Props {
  language: 'en' | 'fr';
}

const t = {
  en: {
    title: 'Financing Calculator',
    subtitle: 'Educational tool — not a financing offer',
    dealPrice: 'Business Asking Price ($)',
    downPayment: 'Down Payment (%)',
    ebitda: 'Adjusted EBITDA ($)',
    interestRate: 'Interest Rate (%)',
    termYears: 'Loan Term (years)',
    annuity: 'Annuity / Conventional',
    sba: 'SBA Loan',
    lbo: 'LBO / Senior Debt',
    monthlyPayment: 'Monthly Payment',
    annualDebtService: 'Annual Debt Service',
    dscr: 'DSCR',
    dscrGood: 'Good coverage',
    dscrWarning: 'Marginal coverage',
    dscrBad: 'Insufficient coverage',
    loanAmount: 'Loan Amount',
    equityRequired: 'Equity Required',
    disclaimer: 'This calculator is for educational purposes only. It does not constitute a financing offer. Consult a financial advisor before making any acquisition decision.',
    lboEquity: 'LBO Equity (%)',
    seniorDebt: 'Senior Debt',
    mezz: 'Mezzanine / Seller Financing',
    mezzRate: 'Mezzanine Rate (%)',
    mezzTerm: 'Mezzanine Term (years)',
    totalService: 'Total Annual Debt Service',
    pctPrice: '% of Price',
  },
  fr: {
    title: 'Calculateur de Financement',
    subtitle: 'Outil éducatif — pas une offre de financement',
    dealPrice: "Prix demandé de l'entreprise ($)",
    downPayment: 'Mise de fonds (%)',
    ebitda: 'EBITDA Ajusté ($)',
    interestRate: "Taux d'intérêt (%)",
    termYears: 'Durée du prêt (années)',
    annuity: 'Annuité / Conventionnel',
    sba: 'Prêt SBA',
    lbo: 'LBO / Dette Senior',
    monthlyPayment: 'Paiement mensuel',
    annualDebtService: 'Service de dette annuel',
    dscr: 'DSCR',
    dscrGood: 'Couverture suffisante',
    dscrWarning: 'Couverture marginale',
    dscrBad: 'Couverture insuffisante',
    loanAmount: 'Montant du prêt',
    equityRequired: 'Capitaux requis',
    disclaimer: "Ce calculateur est à titre éducatif uniquement. Il ne constitue pas une offre de financement. Consultez un conseiller financier avant toute décision d'acquisition.",
    lboEquity: 'Équité LBO (%)',
    seniorDebt: 'Dette senior',
    mezz: 'Mezzanine / Financement vendeur',
    mezzRate: 'Taux mezzanine (%)',
    mezzTerm: 'Durée mezzanine (années)',
    totalService: 'Service de dette total annuel',
    pctPrice: '% du prix',
  },
};

function calcAnnuityPayment(principal: number, annualRate: number, termYears: number): number {
  if (principal <= 0 || termYears <= 0) return 0;
  if (annualRate === 0) return principal / (termYears * 12);
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

function getDscrColor(dscr: number): string {
  if (dscr >= 1.25) return 'bg-green-100 text-green-800';
  if (dscr >= 1.0) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

function getDscrLabel(dscr: number, labels: typeof t.en): string {
  if (dscr >= 1.25) return labels.dscrGood;
  if (dscr >= 1.0) return labels.dscrWarning;
  return labels.dscrBad;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function DealFinancingCalculator({ language }: Props) {
  const labels = t[language];

  const [price, setPrice] = useState(800000);
  const [ebitda, setEbitda] = useState(200000);

  const [annDown, setAnnDown] = useState(20);
  const [annRate, setAnnRate] = useState(7.5);
  const [annTerm, setAnnTerm] = useState(10);

  const [sbaDown, setSbaDown] = useState(10);
  const [sbaRate, setSbaRate] = useState(8.5);
  const [sbaTerm, setSbaTerm] = useState(10);

  const [lboEquity, setLboEquity] = useState(30);
  const [lboSrRate, setLboSrRate] = useState(8.0);
  const [lboSrTerm, setLboSrTerm] = useState(7);
  const [lboMezzPct, setLboMezzPct] = useState(20);
  const [lboMezzRate, setLboMezzRate] = useState(12);
  const [lboMezzTerm, setLboMezzTerm] = useState(5);

  const annuity = useMemo(() => {
    const loan = price * (1 - annDown / 100);
    const monthly = calcAnnuityPayment(loan, annRate, annTerm);
    const annual = monthly * 12;
    const dscr = annual > 0 ? ebitda / annual : 0;
    return { loan, monthly, annual, dscr, equity: price * (annDown / 100) };
  }, [price, ebitda, annDown, annRate, annTerm]);

  const sba = useMemo(() => {
    const loan = price * (1 - sbaDown / 100);
    const monthly = calcAnnuityPayment(loan, sbaRate, sbaTerm);
    const annual = monthly * 12;
    const dscr = annual > 0 ? ebitda / annual : 0;
    return { loan, monthly, annual, dscr, equity: price * (sbaDown / 100) };
  }, [price, ebitda, sbaDown, sbaRate, sbaTerm]);

  const lbo = useMemo(() => {
    const equityAmt = price * (lboEquity / 100);
    const srPct = 1 - lboEquity / 100 - lboMezzPct / 100;
    const srLoan = price * Math.max(srPct, 0);
    const mezzLoan = price * (lboMezzPct / 100);
    const srMonthly = calcAnnuityPayment(srLoan, lboSrRate, lboSrTerm);
    const mezzMonthly = calcAnnuityPayment(mezzLoan, lboMezzRate, lboMezzTerm);
    const annual = (srMonthly + mezzMonthly) * 12;
    const dscr = annual > 0 ? ebitda / annual : 0;
    return { equityAmt, srLoan, mezzLoan, srMonthly, mezzMonthly, annual, dscr };
  }, [price, ebitda, lboEquity, lboSrRate, lboSrTerm, lboMezzPct, lboMezzRate, lboMezzTerm]);

  const numInput = (val: number, setter: (v: number) => void, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type="number"
        value={val}
        onChange={(e) => setter(parseFloat(e.target.value) || 0)}
        className="h-8 text-sm"
      />
    </div>
  );

  const dscrBadge = (dscr: number) => (
    <div className="flex items-center gap-2">
      <span className="font-bold text-lg">{dscr.toFixed(2)}x</span>
      <Badge variant="outline" className={getDscrColor(dscr)}>
        {getDscrLabel(dscr, labels)}
      </Badge>
    </div>
  );

  const ResultRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-semibold text-sm">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#1D314D] text-white">
          <Calculator className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#1D314D]">{labels.title}</h2>
          <p className="text-sm text-muted-foreground">{labels.subtitle}</p>
        </div>
      </div>

      {/* Shared inputs */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {numInput(price, setPrice, labels.dealPrice)}
          {numInput(ebitda, setEbitda, labels.ebitda)}
        </CardContent>
      </Card>

      {/* Structure tabs */}
      <Tabs defaultValue="annuity" className="w-full">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="annuity">{labels.annuity}</TabsTrigger>
          <TabsTrigger value="sba">{labels.sba}</TabsTrigger>
          <TabsTrigger value="lbo">{labels.lbo}</TabsTrigger>
        </TabsList>

        {/* ANNUITY */}
        <TabsContent value="annuity" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{labels.annuity}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {numInput(annDown, setAnnDown, labels.downPayment)}
              {numInput(annRate, setAnnRate, labels.interestRate)}
              {numInput(annTerm, setAnnTerm, labels.termYears)}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <ResultRow label={labels.loanAmount} value={fmt(annuity.loan)} />
              <ResultRow label={labels.equityRequired} value={fmt(annuity.equity)} />
              <ResultRow label={labels.monthlyPayment} value={fmt(annuity.monthly)} />
              <ResultRow label={labels.annualDebtService} value={fmt(annuity.annual)} />
              <div className="flex items-center justify-between pt-3">
                <span className="text-sm font-medium">{labels.dscr}</span>
                {dscrBadge(annuity.dscr)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SBA */}
        <TabsContent value="sba" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{labels.sba}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {numInput(sbaDown, setSbaDown, labels.downPayment)}
              {numInput(sbaRate, setSbaRate, labels.interestRate)}
              {numInput(sbaTerm, setSbaTerm, labels.termYears)}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <ResultRow label={labels.loanAmount} value={fmt(sba.loan)} />
              <ResultRow label={labels.equityRequired} value={fmt(sba.equity)} />
              <ResultRow label={labels.monthlyPayment} value={fmt(sba.monthly)} />
              <ResultRow label={labels.annualDebtService} value={fmt(sba.annual)} />
              <div className="flex items-center justify-between pt-3">
                <span className="text-sm font-medium">{labels.dscr}</span>
                {dscrBadge(sba.dscr)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LBO */}
        <TabsContent value="lbo" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{labels.lbo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {numInput(lboEquity, setLboEquity, labels.lboEquity)}
                {numInput(lboSrRate, setLboSrRate, labels.interestRate)}
                {numInput(lboSrTerm, setLboSrTerm, labels.termYears)}
              </div>
              <div className="pt-3 border-t">
                <p className="text-sm font-medium mb-2">{labels.mezz}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {numInput(lboMezzPct, setLboMezzPct, labels.pctPrice)}
                  {numInput(lboMezzRate, setLboMezzRate, labels.mezzRate)}
                  {numInput(lboMezzTerm, setLboMezzTerm, labels.mezzTerm)}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <ResultRow label={labels.seniorDebt} value={fmt(lbo.srLoan)} />
              <ResultRow label={labels.mezz} value={fmt(lbo.mezzLoan)} />
              <ResultRow label={labels.equityRequired} value={fmt(lbo.equityAmt)} />
              <ResultRow label={labels.totalService} value={fmt(lbo.annual)} />
              <div className="flex items-center justify-between pt-3">
                <span className="text-sm font-medium">{labels.dscr}</span>
                {dscrBadge(lbo.dscr)}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900">{labels.disclaimer}</p>
      </div>
    </div>
  );
}
